import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import type { MessageHandler, ErrorHandler } from "@sumsub/websdk";
import SumsubWebSdk from "@sumsub/websdk-react";
import { useState, useEffect } from "react";
import { Loading, Panel, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetSumsubTokenMutation } from "@/redux/apis/kyc/kycApi";
import { addToast } from "@/utils";

const isSumsubEnabled = import.meta.env.VITE_ENABLE_SUMSUB !== "false";

interface KYCVerificationStepProps {
  onVerificationComplete: () => void;
}

export const KYCVerificationStep = ({ onVerificationComplete }: KYCVerificationStepProps) => {
  const { user } = useKindeAuth();
  const [getSumsubToken, { isLoading }] = useGetSumsubTokenMutation();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSumsubEnabled) {
      onVerificationComplete();
      return;
    }

    getSumsubToken({ role: "Owner" })
      .unwrap()
      .then((response) => {
        setAccessToken(response.token);
      })
      .catch((err) => {
        console.error("Failed to get Sumsub token:", err);
        setError("Failed to initialize verification. Please try again.");
        getErrorMessages(err).forEach((m) => addToast("danger", m));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSumsubToken]);

  const handleSumsubMessage: MessageHandler = (type, payload) => {
    if (type === "idCheck.onApplicantStatusChanged") {
      const { reviewStatus, reviewResult } = payload as {
        reviewStatus?: "init" | "pending" | "completed";
        reviewResult?: { reviewAnswer?: "GREEN" | "YELLOW" | "RED" };
      };

      const isApproved = reviewStatus === "completed" && reviewResult?.reviewAnswer === "GREEN";

      if (isApproved) {
        onVerificationComplete();
      }
    }
  };

  const onError: ErrorHandler = (error) => getErrorMessages(error).forEach((m) => addToast("danger", m));

  // Show error state if token fetch failed
  if (error) {
    return (
      <Panel className="flex h-fit min-h-[600px] w-[400px] flex-col items-center justify-center p-8 transition-all duration-300 ease-in-out">
        <Typography variant="h2" className="mb-4 text-center text-danger">
          {error}
        </Typography>
        <Typography variant="paragraph" className="text-center">
          Please contact support if this issue persists.
        </Typography>
      </Panel>
    );
  }

  // Show centered loading spinner while fetching token
  if (isLoading || !accessToken) {
    return (
      <Panel className="flex h-fit min-h-[600px] w-[400px] items-center justify-center transition-all duration-300 ease-in-out">
        <Loading className="!bg-transparent" />
      </Panel>
    );
  }

  return (
    <Panel className="h-fit min-h-[600px] w-[400px] p-1 transition-all duration-300 ease-in-out">
      <SumsubWebSdk
        accessToken={accessToken}
        expirationHandler={() => Promise.resolve(accessToken)}
        config={{
          lang: "en",
          email: user?.email || "user@example.com",
        }}
        options={{
          addViewportTag: false,
          adaptIframeHeight: true,
        }}
        onMessage={handleSumsubMessage}
        onError={onError}
      />
    </Panel>
  );
};
